import { matchRoutes } from "react-router";
import { initializeFaro, createReactRouterV6DataOptions, ReactIntegration, getWebInstrumentations } from "@grafana/faro-react";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";
import { LogLevel, type Faro, type LogContext, type EventAttributes } from "@grafana/faro-web-sdk";
import posthog, { type CallContext } from "src/services/posthog";

/**
 * Contexto estruturado dos logs. Valores são normalizados para string antes de
 * serem enviados ao Faro (Loki), garantindo campos JSON pesquisáveis.
 */
export type LoggerContext = Record<string, string | number | boolean | null | undefined>;

type FaroApi = {
  setUser: (user: Record<string, string>) => void;
  pushLog: (args: unknown[], options?: { level?: LogLevel; context?: LogContext }) => void;
  pushError: (error: Error, options?: { type?: string; context?: LogContext }) => void;
  pushEvent: (name: string, attributes?: EventAttributes) => void;
  pushMeasurement: (payload: { type: string; values: Record<string, number> }, options?: { context?: LogContext }) => void;
};

const faroStub: { api: FaroApi } = {
  api: {
    setUser: () => {},
    pushLog: () => {},
    pushError: () => {},
    pushEvent: () => {},
    pushMeasurement: () => {},
  },
};

/**
 * Instância do Faro. Quando `VITE_SHOULD_USE_FARO=false` permanece como stub
 * (logs vão apenas para o console). Em produção é substituída pela instância real.
 */
export let faro: Faro | typeof faroStub = faroStub;

const SHOULD_USE_FARO = import.meta.env.VITE_SHOULD_USE_FARO === "true";

export type LogLevelName = "trace" | "debug" | "info" | "log" | "warn" | "error";

const LOG_LEVEL_ORDER: Record<LogLevelName, number> = {
  trace: 0,
  debug: 1,
  info: 2,
  log: 3,
  warn: 4,
  error: 5,
};

const CONFIGURED_LOG_LEVEL = (import.meta.env.VITE_LOG_LEVEL as LogLevelName) || "info";

let runtimeLogLevelOverride: LogLevelName | null = null;

/**
 * Sobrescreve o nível de log em runtime (ex.: via LaunchDarkly).
 * Passar um valor inválido ou nulo restaura o default do ambiente.
 */
export const setLogLevel = (level: unknown) => {
  if (typeof level === "string" && level in LOG_LEVEL_ORDER) {
    runtimeLogLevelOverride = level as LogLevelName;
  } else {
    runtimeLogLevelOverride = null;
  }
};

export const getDefaultLogLevel = (): LogLevelName => CONFIGURED_LOG_LEVEL;

const getCurrentLogLevel = (): LogLevelName => runtimeLogLevelOverride ?? CONFIGURED_LOG_LEVEL;

const shouldLog = (level: LogLevelName) => {
  const currentOrder = LOG_LEVEL_ORDER[getCurrentLogLevel()] ?? LOG_LEVEL_ORDER.info;
  return (LOG_LEVEL_ORDER[level] ?? LOG_LEVEL_ORDER.info) >= currentOrder;
};

/**
 * Contexto global de telemetria (ex.: a chamada em andamento). É mesclado em
 * todos os logs/eventos do Faro e, simultaneamente, registrado como super
 * property no PostHog, garantindo que `callId` acompanhe os dois destinos.
 */
let telemetryContext: LoggerContext = {};

export const setCallContext = (context: CallContext) => {
  telemetryContext = { ...telemetryContext, ...context };
  posthog.setCallContext(context);
};

export const clearCallContext = () => {
  telemetryContext = {};
  posthog.clearCallContext();
};

export const getCallContext = (): LoggerContext => telemetryContext;

let faroReady = false;

export const initFaro = () => {
  if (!SHOULD_USE_FARO) return faro;

  faro = initializeFaro({
    url: "https://faro-collector-prod-sa-east-1.grafana.net/collect/ce848e10d339fd908d9e818b533aecce",
    app: {
      name: "Softphone Web",
      version: import.meta.env.PACKAGE_VERSION,
      environment: import.meta.env.MODE,
    },

    instrumentations: [
      // Mandatory, omits default instrumentations otherwise.
      ...getWebInstrumentations(),

      // Tracing package to get end-to-end visibility for HTTP requests.
      new TracingInstrumentation(),

      // React integration for React applications.
      new ReactIntegration({
        router: createReactRouterV6DataOptions({
          matchRoutes,
        }),
      }),
    ],
  });

  faroReady = true;

  return faro;
};

const toLogContext = (context?: LoggerContext): LogContext | undefined => {
  const merged = { ...telemetryContext, ...context };

  const hasValues = Object.values(merged).some((value) => value !== undefined && value !== null);
  if (!hasValues) return undefined;

  const normalized: LogContext = {};

  for (const [key, value] of Object.entries(merged)) {
    if (value === undefined || value === null) continue;

    normalized[key] = typeof value === "string" ? value : JSON.stringify(value);
  }

  return normalized;
};

const makeLogFn =
  (levelName: LogLevelName, level: LogLevel, consoleFn: (...args: any[]) => void) =>
  (message: string, context?: LoggerContext) => {
    if (!shouldLog(levelName)) return;

    if (!faroReady) {
      consoleFn(message, context ?? "");
      return;
    }

    faro.api.pushLog([message], { level, context: toLogContext(context) });
  };

export const logInfo = makeLogFn("info", LogLevel.INFO, console.info);
export const logDebug = makeLogFn("debug", LogLevel.DEBUG, console.debug);
export const logTrace = makeLogFn("trace", LogLevel.TRACE, console.trace);
export const logWarn = makeLogFn("warn", LogLevel.WARN, console.warn);

export const logError = (message: string, error?: unknown, context?: LoggerContext) => {
  if (!shouldLog("error")) return;

  const normalizedContext = toLogContext(context);

  if (!faroReady) {
    console.error(message, error ?? "", normalizedContext ?? "");
    return;
  }

  if (error instanceof Error) {
    faro.api.pushError(error, { type: message, context: normalizedContext });
  } else {
    faro.api.pushError(new Error(message), { type: message, context: normalizedContext });
  }
};

export const logEvent = (name: string, attributes?: LoggerContext) => {
  if (!shouldLog("debug")) return;

  if (!faroReady) {
    console.log(`[event] ${name}`, attributes ?? "");
    return;
  }

  faro.api.pushEvent(name, toLogContext(attributes) as EventAttributes);
};

/**
 * Converte um LoggerContext para propriedades nativas aceitas pelo PostHog,
 * descartando `null`/`undefined` sem transformar tudo em string (o PostHog
 * preserva os tipos primitivos nativamente).
 */
const toPostHogProperties = (context?: LoggerContext): Record<string, string | number | boolean> | undefined => {
  if (!context) return undefined;

  const result: Record<string, string | number | boolean> = {};

  for (const [key, value] of Object.entries(context)) {
    if (value === undefined || value === null) continue;

    result[key] = value;
  }

  return Object.keys(result).length ? result : undefined;
};

/**
 * Evento de produto: enviado ao PostHog (capture) e espelhado no Faro (event).
 * O contexto da chamada (callId, aderenteSlug, callType) é anexado
 * automaticamente a ambos os destinos.
 */
export const trackEvent = (name: string, properties?: LoggerContext) => {
  posthog.capture(name, toPostHogProperties(properties));
  logEvent(name, properties);
};

export const logMeasurement = (type: string, values: Record<string, number>, context?: LoggerContext) => {
  if (!shouldLog("debug")) return;

  if (!faroReady) {
    console.log(`[measurement] ${type}`, values, context ?? "");
    return;
  }

  faro.api.pushMeasurement({ type, values }, { context: toLogContext(context) });
};

/**
 * Conector de logs do SIP.js. Encaminha mensagens internas da lib para o Faro,
 * desativando o console nativo (`logBuiltinEnabled: false`).
 */
export const createSipLogConnector = (context: LoggerContext) => (level: string, category: string, label: string | undefined, content: string) => {
  const levelMap: Record<string, (message: string, context?: LoggerContext) => void> = {
    debug: logDebug,
    log: logInfo,
    warn: logWarn,
    error: logError,
  };

  const logFn = levelMap[level] ?? logInfo;
  const sipContext: LoggerContext = { ...context, source: "sip.js", sipLevel: level, category, label: label ?? null };

  logFn(content, sipContext);
};
